import React from 'react';
import { RaceType } from '../../types/athlete';
import { Calendar, Flag } from 'lucide-react';

interface StepRaceDetailsProps {
  raceDetails: {
    raceType: RaceType | null;
    raceDate: string | null;
    name: string;
    description: string;
  };
  onUpdate: (updates: Partial<{
    raceType: RaceType | null;
    raceDate: string | null;
    name: string;
    description: string;
  }>) => void;
}

const raceOptions: { type: RaceType; label: string; distance: string; icon: string }[] = [
  { type: 'sprint', label: 'Sprint Triathlon', distance: '750m swim / 20km bike / 5km run', icon: '⚡' },
  { type: 'olympic', label: 'Olympic Distance', distance: '1.5km swim / 40km bike / 10km run', icon: '🏅' },
  { type: '70.3', label: 'Half Ironman (70.3)', distance: '1.9km swim / 90km bike / 21.1km run', icon: '🔶' },
  { type: 'ironman', label: 'Full Ironman', distance: '3.8km swim / 180km bike / 42.2km run', icon: '💪' },
];

const StepRaceDetails: React.FC<StepRaceDetailsProps> = ({ raceDetails, onUpdate }) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Race Details</h2>
        <p className="text-muted">Tell us about the race you're training for.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#e2e8f0] mb-4">
          <span className="flex items-center space-x-2">
            <Flag size={16} />
            <span>Race Distance</span>
          </span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {raceOptions.map((option) => (
            <button
              key={option.type}
              onClick={() => onUpdate({ raceType: option.type })}
              className={`card-base p-4 text-left transition-all ${
                raceDetails.raceType === option.type
                  ? 'ring-2 ring-[#a855f7] bg-[rgba(168,85,247,0.1)]'
                  : 'hover:bg-[#1e293b]'
              }`}
            >
              <div className="flex items-start space-x-3">
                <span className="text-2xl">{option.icon}</span>
                <div>
                  <p className="font-semibold">{option.label}</p>
                  <p className="text-sm text-muted mt-1">{option.distance}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
          <span className="flex items-center space-x-2">
            <Calendar size={16} />
            <span>Race Date</span>
          </span>
        </label>
        <input
          type="date"
          value={raceDetails.raceDate || ''}
          onChange={(e) => onUpdate({ raceDate: e.target.value })}
          className="w-full md:w-64"
          min={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
            <span className="flex items-center space-x-2">
              <span>Plan Name (optional)</span>
            </span>
          </label>
          <input
            type="text"
            placeholder="e.g., My Ironman Journey"
            value={raceDetails.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default StepRaceDetails;
